import {
  capitalize,
  cn,
  EDRResponseName,
  EDRResponses,
  ExitCode,
  ExitCodeGroup,
  getStatusColor,
} from "@theprelude/core";
import {
  CheckmarkIcon,
  CloseIcon,
  RefreshIcon,
  ResponseIcon,
  ShieldCheckIcon,
  ShieldFailIcon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@theprelude/ds";
import { ExpectedOutcome, ExpectedResult, OptionalODPFields } from "@/lib/odp";
import { ExpectedResultIcon } from "../expected-result-icon";
import { Flag } from "../flag/flag";
import { Unavailable } from "./unavaliable";

export function BenchmarkColumnValue({
  status,
}: {
  status: number | undefined;
}) {
  return status == undefined ? (
    <Unavailable />
  ) : (
    <div className="min-w-24 whitespace-nowrap text-secondary-foreground">
      {status}%
    </div>
  );
}

export function PercentageValue({ status }: { status: number | undefined }) {
  if (status == undefined) {
    return <Unavailable />;
  }

  const color = getStatusColor(status);

  return (
    <div
      className={cn(
        color,
        "flex items-center gap-1 whitespace-nowrap font-medium dark:font-normal",
      )}
    >
      {status}%
    </div>
  );
}

export const StatusResultIcon: React.FC<{ percent: number }> = ({
  percent,
}) => {
  if (percent === 100) {
    return <ShieldCheckIcon className="h-4 w-4 fill-status-green" />;
  }

  return <ShieldFailIcon className="h-4 w-4 fill-status-red" />;
};

export function ProtectionStatus({ status }: { status: number | undefined }) {
  if (status == undefined) {
    return <Unavailable />;
  }

  return (
    <div
      className={cn(
        "flex min-w-24 items-center gap-1 whitespace-nowrap",
        status === 100 ? "text-status-green" : "text-status-red",
      )}
    >
      <StatusResultIcon percent={status} />
      {status === 100 ? "Protected" : "Unprotected"}
    </div>
  );
}

export function ExpectedProtectionLevel({
  result,
}: {
  result?: ExpectedResult;
}) {
  if (!result || !result.expectation) {
    return <Unavailable />;
  }

  const color = getStatusColor(result.percent);

  return (
    <div className="flex w-full items-center justify-between whitespace-nowrap pr-1 font-medium dark:font-normal">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <ExpectedResultIcon
          className="size-4 text-secondary-foreground"
          name={result.expectation}
        />
        {capitalize(result.expectation)}
      </div>

      <span className={cn(color)}>
        {result.percent !== undefined && `${result.percent}%`}
      </span>
    </div>
  );
}

export function ExpectedOutcomeBlock({
  expectedOutcome,
  observed,
  detected,
  status,
}: {
  expectedOutcome: ExpectedOutcome | undefined;
  observed: string | undefined;
  detected: string | undefined;
  status: ExitCode;
}) {
  if (!expectedOutcome) {
    return <Unavailable />;
  }
  const isPass = (ExitCodeGroup.PROTECTED as unknown as number[]).includes(
    status,
  );
  return (
    <div className="flex items-center justify-between gap-2 whitespace-nowrap font-medium dark:font-normal">
      <div className="flex w-[90px] items-center gap-1 whitespace-nowrap text-primary-foreground">
        <ExpectedResultIcon
          className="size-4"
          name={expectedOutcome.expectation}
        />
        {capitalize(expectedOutcome.expectation)}
      </div>

      <Flag features={["observed_detected"]}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap font-medium dark:font-normal",
                  expectedOutcome.isExpected === "yes" && "text-status-green",
                  expectedOutcome.isExpected === "no" && "text-status-red",
                  expectedOutcome.isExpected === "pending" &&
                    "text-secondary-foreground",
                )}
              >
                {expectedOutcome.isExpected === "yes" ? (
                  <>
                    <CheckmarkIcon />
                    Yes
                  </>
                ) : expectedOutcome.isExpected === "no" ? (
                  <>
                    <CloseIcon />
                    No
                  </>
                ) : (
                  <>
                    <RefreshIcon />
                    Pending
                  </>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent className="w-48">
              <div className="flex flex-col gap-4 text-secondary-foreground text-sm">
                {Object.entries({
                  observed,
                  detected,
                  prevented: isPass,
                }).map(([name, value]) => {
                  const responseName = name.toUpperCase() as EDRResponseName;
                  const responseEnum = EDRResponses[responseName];
                  return (
                    <div
                      className="flex items-center justify-between"
                      key={name}
                    >
                      <div className="flex items-center justify-between gap-1 text-primary-foreground">
                        <ResponseIcon expectedResponse={responseEnum} />
                        <span>{capitalize(name)}</span>
                      </div>
                      {value === "pending" ? (
                        <span className="flex items-center gap-1 text-secondary-foreground">
                          <RefreshIcon /> Pending
                        </span>
                      ) : value ? (
                        <span className="flex items-center gap-1 text-status-green">
                          <CheckmarkIcon /> Yes
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-status-red">
                          <CloseIcon /> No
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Flag>
    </div>
  );
}

export function PersonalExpectedOutcomeBlock({
  expectedResult,
  odpFields,
}: {
  expectedResult: ExpectedResult | undefined;
  odpFields: OptionalODPFields;
}) {
  if (!expectedResult || !expectedResult.expectation) {
    return <Unavailable />;
  }
  return (
    <div className="flex w-[140px] items-center justify-between gap-2 whitespace-nowrap font-medium dark:font-normal">
      <div className="flex items-center gap-1 whitespace-nowrap text-primary-foreground">
        <ExpectedResultIcon
          className="size-4"
          name={expectedResult.expectation}
        />
        {capitalize(expectedResult.expectation)}
      </div>

      <Flag features={["observed_detected"]}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <span
                className={cn(
                  "flex items-center gap-1 whitespace-nowrap font-medium dark:font-normal",
                  expectedResult.percent
                    ? "text-status-green"
                    : "text-status-red",
                )}
              >
                {expectedResult.percent ? <CheckmarkIcon /> : <CloseIcon />}
                {expectedResult.percent ? "Yes" : "No"}
              </span>
            </TooltipTrigger>
            <TooltipContent className="w-40">
              <div className="flex flex-col gap-4 text-secondary-foreground text-sm">
                {Object.entries(odpFields).map(([name, value]) => {
                  const responseName = name.toUpperCase() as EDRResponseName;
                  const responseEnum = EDRResponses[responseName];
                  return (
                    <div
                      className="flex items-center justify-between"
                      key={name}
                    >
                      <div className="flex items-center justify-between gap-1 text-primary-foreground">
                        <ResponseIcon expectedResponse={responseEnum} />
                        <span>{capitalize(name)}</span>
                      </div>
                      {value ? (
                        <span className="flex items-center gap-1 text-status-green">
                          <CheckmarkIcon /> Yes
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-status-red">
                          <CloseIcon /> No
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Flag>
    </div>
  );
}
